import { FlatTreeControl } from '@angular/cdk/tree';
import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  MatTree,
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodePadding,
  MatTreeNodeToggle
} from '@angular/material/tree';
import { forkJoin, map, mergeMap, Observable, of } from 'rxjs';
import { ChildProject, Project } from '../../../../models/project.module';
import { NewProjectService } from '../../../../services/newProject.service';
import { ProjectService } from '../../../../services/project.service';

interface FlatProject {
  level: number;
  expandable: boolean;
  id: string;
  name: string;
  path: string;
  is_project: boolean;
  project_id: string;
  children?: FlatProject[];
  unloadedChildren?: ChildProject[];
  childrenLoaded?: boolean;
  [key: string]: any;
}

export interface MoveDialogData {
  itemType: 'folder' | 'area' | 'areas';
  itemId?: string;
  itemIds?: string[];
  currentProjectId: string;
  currentFolderId?: string;
  currentProjectName?: string;
  currentFolderName?: string;
  itemName?: string;
  itemsCount?: number;
}

@Component({
  selector: 'frmg-move-dialog',
  templateUrl: './move-dialog.component.html',
  styleUrls: ['./move-dialog.component.scss'],
  imports: [
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    NgClass,
    MatIconButton,
    MatTreeNodeToggle,
    MatIcon,
    MatProgressBar,
    MatButton
  ]
})
export class MoveDialogComponent implements OnInit {
  public folders: Array<Project> = [];
  public isLoading = true;
  public selectedFolder: Project | null = null;
  public currentPath: string = '';
  private pathToCurrentFolder: Project[] = [];

  private _transformer = (node: FlatProject, level: number) => {
    const expandable =
      (node.children && node.children.length > 0) ||
      (node.unloadedChildren && node.unloadedChildren.length > 0);

    return {
      expandable,
      level,
      ...node
    };
  };

  public treeControl = new FlatTreeControl<FlatProject>(
    (node) => node.level,
    (node) => node.expandable
  );

  public treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  public dataSource = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  constructor(
    private dialogRef: MatDialogRef<MoveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MoveDialogData,
    private projectService: ProjectService,
    private newProjectService: NewProjectService,
    private cd: ChangeDetectorRef
  ) {
    if (data.currentFolderId && data.currentFolderName) {
      this.currentPath = data.currentFolderId;
    } else if (data.currentProjectId && data.currentProjectName) {
      this.currentPath = data.currentProjectId;
    }
  }

  ngOnInit(): void {
    this.loadProjectsTree();

    this.treeControl.expansionModel.changed.subscribe((change) => {
      change.added.forEach((node) => {
        setTimeout(() => {
          this.loadNodeChildren(node);
        }, 0);
      });
    });
  }

  private loadProjectsTree(): void {
    this.isLoading = true;

    forkJoin({
      root: this.newProjectService.getProjectsByPath('', 'root', 'name:asc'),
      shared: this.newProjectService.getProjectsByPath(
        '',
        'shared',
        'name:asc'
      )
    }).subscribe({
      next: ({ root, shared }) => {
        const allProjects = [...root, ...shared].filter(
          (project: Project) => !project.is_archived
        );

        if (allProjects.length === 0) {
          this.dataSource.data = [];
          this.isLoading = false;
          this.cd.detectChanges();
          return;
        }

        forkJoin(
          allProjects.map((project) =>
            this.projectService.getProjectById(project.id)
          )
        ).subscribe({
          next: (projectsWithDetails) => {
            if (this.currentPath) {
              this.loadPathToCurrentFolder(projectsWithDetails).subscribe({
                next: (treeData) => {
                  this.dataSource.data = treeData;
                  this.isLoading = false;
                  this.cd.detectChanges();

                  setTimeout(() => {
                    this.expandPathToCurrentFolder();
                  }, 0);
                },
                error: (error) => {
                  console.error('Error loading path to current folder:', error);
                  this.dataSource.data = this.convertProjectsToFlatProjects(
                    projectsWithDetails
                  );
                  this.isLoading = false;
                  this.cd.detectChanges();
                }
              });
            } else {
              this.dataSource.data = this.convertProjectsToFlatProjects(
                projectsWithDetails
              );

              this.isLoading = false;
              this.cd.detectChanges();
            }
          },
          error: (error) => {
            console.error('Error loading projects:', error);
            this.isLoading = false;
            this.cd.detectChanges();
          }
        });
      },
      error: (error) => {
        console.error('Error loading projects:', error);
        this.isLoading = false;
        this.cd.detectChanges();
      }
    });
  }

  /**
   * Загружает путь от корня до текущей папки (предзагрузка)
   * Остальные узлы остаются с unloadedChildren для ленивой загрузки
   */
  private loadPathToCurrentFolder(
    rootProjects: Project[]
  ): Observable<FlatProject[]> {
    return this.findAndLoadPathToNode(rootProjects, this.currentPath).pipe(
      map((pathProjects) => {
        this.pathToCurrentFolder = pathProjects;

        return this.convertProjectsToFlatProjectsWithPath(
          rootProjects,
          pathProjects,
          null
        );
      })
    );
  }

  /**
   * Находит и загружает путь от корня до целевого узла
   */
  private findAndLoadPathToNode(
    projects: Project[],
    targetId: string,
    pathSoFar: Project[] = []
  ): Observable<Project[]> {
    for (const project of projects) {
      if (project.id === targetId) {
        return of([...pathSoFar, project]);
      }

      const children = (project.children || []) as ChildProject[];
      const activeChildren = children.filter(
        (child: ChildProject) => !child.is_archived
      );

      if (activeChildren.length > 0) {
        return forkJoin(
          activeChildren.map((child) =>
            this.projectService.getProjectById(child.id)
          )
        ).pipe(
          mergeMap((childProjects) => {
            const result = this.findAndLoadPathToNode(childProjects, targetId, [
              ...pathSoFar,
              project
            ]);

            return result.pipe(
              map((path) => {
                if (
                  path.length > pathSoFar.length &&
                  path[path.length - 1].id === targetId
                ) {
                  return path;
                }
                return pathSoFar;
              })
            );
          })
        );
      }
    }

    return of(pathSoFar);
  }

  /**
   * Конвертирует проекты в FlatProject формат, предзагружая путь к текущей папке
   * Для узлов на пути - загружаем только детей, которые тоже на пути
   * Остальные дети остаются для ленивой загрузки
   */
  private convertProjectsToFlatProjectsWithPath(
    projects: Project[],
    pathProjects: Project[],
    parentProjectId: string | null,
    level: number = 0
  ): FlatProject[] {
    if (!projects || projects.length === 0) {
      return [];
    }

    const pathIds = new Set(pathProjects.map((p) => p.id));

    return projects.map((project) => {
      const projectId = project.project_id || project.id;
      const children = (project.children || []) as ChildProject[];
      const activeChildren = children.filter(
        (child: ChildProject) => !child.is_archived
      );

      let flatChildren: FlatProject[] = [];
      let unloadedChildren: ChildProject[] = [];

      if (pathIds.has(project.id) && activeChildren.length > 0) {
        const pathProject = pathProjects.find((p) => p.id === project.id);
        if (pathProject) {
          const isParentOfMovingFolder =
            this.data.itemType === 'folder' &&
            this.data.itemId &&
            activeChildren.some(
              (child: ChildProject) => child.id === this.data.itemId
            );

          if (isParentOfMovingFolder) {
            const childrenOnPath: Project[] = [];
            const childrenNotOnPath: ChildProject[] = [];

            activeChildren.forEach((child: ChildProject) => {
              if (pathIds.has(child.id)) {
                const childPathProject = pathProjects.find(
                  (p) => p.id === child.id
                );
                if (childPathProject) {
                  childrenOnPath.push(childPathProject);
                }
              } else {
                childrenNotOnPath.push(child);
              }
            });

            if (childrenOnPath.length > 0) {
              flatChildren = childrenOnPath.map((childProject: Project) => {
                return this.convertProjectsToFlatProjectsWithPath(
                  [childProject],
                  pathProjects,
                  projectId,
                  level + 1
                )[0];
              });
            }

            if (childrenNotOnPath.length > 0) {
              forkJoin(
                childrenNotOnPath.map((child) =>
                  this.projectService.getProjectById(child.id)
                )
              ).subscribe({
                next: (childrenProjects) => {
                  const flatChildrenNotOnPath: FlatProject[] =
                    childrenProjects.map((childProject: Project) => {
                      const childChildren = (childProject.children ||
                        []) as ChildProject[];
                      const activeChildChildren = childChildren.filter(
                        (c: ChildProject) => !c.is_archived
                      );
                      return {
                        id: childProject.id,
                        name: childProject.name,
                        path: childProject.path || '',
                        project_id: projectId,
                        is_project: childProject.is_project || false,
                        children: [],
                        unloadedChildren:
                          activeChildChildren.length > 0
                            ? activeChildChildren
                            : undefined,
                        childrenLoaded: false,
                        level: level + 1,
                        expandable: activeChildChildren.length > 0
                      };
                    });

                  this.updateParentNodeChildrenInPlace(
                    project.id,
                    flatChildrenNotOnPath
                  );
                },
                error: (error) => {
                  console.error('Error loading children not on path:', error);
                }
              });

              const tempFlatChildren: FlatProject[] = childrenNotOnPath.map(
                (child: ChildProject) => {
                  return {
                    id: child.id,
                    name: child.name,
                    path: child.path || '',
                    project_id: projectId,
                    is_project: false,
                    children: [],
                    unloadedChildren: undefined,
                    childrenLoaded: false,
                    level: level + 1,
                    expandable: true
                  };
                }
              );

              flatChildren = [...flatChildren, ...tempFlatChildren];
            }
          } else {
            const childrenOnPath: any[] = [];
            const childrenNotOnPath: ChildProject[] = [];

            activeChildren.forEach((child: ChildProject) => {
              if (pathIds.has(child.id)) {
                const childPathProject = pathProjects.find(
                  (p) => p.id === child.id
                );
                if (childPathProject) {
                  childrenOnPath.push(childPathProject);
                }
              } else {
                childrenNotOnPath.push(child);
              }
            });

            if (childrenOnPath.length > 0) {
              flatChildren = childrenOnPath.map((childProject: Project) => {
                return this.convertProjectsToFlatProjectsWithPath(
                  [childProject],
                  pathProjects,
                  projectId,
                  level + 1
                )[0];
              });
            }

            unloadedChildren = childrenNotOnPath;
          }
        }
      } else {
        unloadedChildren = activeChildren;
      }

      const allChildrenLoaded = unloadedChildren.length === 0;

      return {
        id: project.id,
        name: project.name,
        path: project.path || '',
        project_id: projectId,
        is_project: project.is_project || false,
        children: flatChildren,
        unloadedChildren:
          unloadedChildren.length > 0 ? unloadedChildren : undefined,
        childrenLoaded: allChildrenLoaded,
        level,
        expandable: flatChildren.length > 0 || unloadedChildren.length > 0
      };
    });
  }

  private expandPathToCurrentFolder(): void {
    if (!this.currentPath || this.pathToCurrentFolder.length === 0) {
      return;
    }

    const pathIds = new Set(this.pathToCurrentFolder.map((p) => p.id));

    const rootProjectId =
      this.pathToCurrentFolder.length > 0
        ? this.pathToCurrentFolder[0].id
        : null;

    const expandPath = (attempts = 0) => {
      const maxAttempts = 20;
      const dataNodes = this.treeControl.dataNodes;

      if (dataNodes.length === 0 && attempts < maxAttempts) {
        requestAnimationFrame(() => expandPath(attempts + 1));
        return;
      }

      pathIds.forEach((nodeId) => {
        const node = dataNodes.find((n) => n.id === nodeId);
        if (node && node.expandable) {
          if (!this.treeControl.isExpanded(node)) {
            this.treeControl.expand(node);
          }
        }
      });

      dataNodes.forEach((node) => {
        if (node.level === 0 && node.is_project && node.id !== rootProjectId) {
          if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
          }
        }
      });

      this.cd.detectChanges();
    };

    requestAnimationFrame(() => {
      expandPath();
    });
  }

  public loadNodeChildren(node: FlatProject): void {
    const freshNode = this.treeControl.dataNodes.find((n) => n.id === node.id);
    if (!freshNode) {
      return;
    }

    if (
      freshNode.childrenLoaded &&
      (!freshNode.unloadedChildren || freshNode.unloadedChildren.length === 0)
    ) {
      return;
    }

    if (freshNode.unloadedChildren && freshNode.unloadedChildren.length > 0) {
      const unloadedChildren = freshNode.unloadedChildren.filter(
        (child: ChildProject) => !child.is_archived
      );

      if (unloadedChildren.length > 0) {
        forkJoin(
          unloadedChildren.map((child) =>
            this.projectService.getProjectById(child.id)
          )
        ).subscribe({
          next: (childProjects) => {
            const flatChildren: FlatProject[] = childProjects.map((project) => {
              const children = (project.children || []) as ChildProject[];
              const activeChildren = children.filter(
                (child: ChildProject) => !child.is_archived
              );

              return {
                id: project.id,
                name: project.name,
                path: project.path || '',
                project_id:
                  freshNode.project_id || project.project_id || project.id,
                is_project: project.is_project || false,
                children: [],
                unloadedChildren: activeChildren,
                childrenLoaded: false,
                level: (freshNode.level || 0) + 1,
                expandable: activeChildren.length > 0
              };
            });

            this.updateNodeWithLoadedChildren(freshNode.id, flatChildren);
          },
          error: (error) => {
            console.error('Error loading node children:', error);
          }
        });
      }
    } else {
      this.projectService.getProjectById(freshNode.id).subscribe({
        next: (fullProject) => {
          const children = (fullProject.children || []) as ChildProject[];
          const activeChildren = children.filter(
            (child: ChildProject) => !child.is_archived
          );

          if (activeChildren.length > 0) {
            forkJoin(
              activeChildren.map((child) =>
                this.projectService.getProjectById(child.id)
              )
            ).subscribe({
              next: (childProjects) => {
                const flatChildren: FlatProject[] = childProjects.map(
                  (project) => {
                    const childChildren = (project.children ||
                      []) as ChildProject[];
                    const activeChildChildren = childChildren.filter(
                      (child: ChildProject) => !child.is_archived
                    );

                    return {
                      id: project.id,
                      name: project.name,
                      path: project.path || '',
                      project_id:
                        freshNode.project_id ||
                        project.project_id ||
                        project.id,
                      is_project: project.is_project || false,
                      children: [],
                      unloadedChildren: activeChildChildren,
                      childrenLoaded: false,
                      level: (freshNode.level || 0) + 1,
                      expandable: activeChildChildren.length > 0
                    };
                  }
                );

                this.updateNodeWithLoadedChildren(freshNode.id, flatChildren);
              },
              error: (error) => {
                console.error('Error loading node children:', error);
              }
            });
          }
        },
        error: (error) => {
          console.error('Error loading node:', error);
        }
      });
    }
  }

  private updateNodeWithLoadedChildren(
    nodeId: string,
    loadedChildren: FlatProject[]
  ): void {
    const expandedNodeIds = new Set<string>();
    this.treeControl.expansionModel.selected.forEach((node) => {
      expandedNodeIds.add(node.id);
    });

    const currentData = this.dataSource.data;
    const updated = this.updateNodeChildrenInTree(
      currentData,
      nodeId,
      loadedChildren
    );

    if (updated) {
      this.dataSource.data = [...currentData];

      requestAnimationFrame(() => {
        let attempts = 0;
        const maxAttempts = 20;

        const tryRestoreExpansion = () => {
          attempts++;
          const dataNodes = this.treeControl.dataNodes;

          if (dataNodes.length > 0) {
            let restored = false;

            expandedNodeIds.forEach((nodeId) => {
              const node = dataNodes.find((n) => n.id === nodeId);
              if (node && node.expandable) {
                if (!this.treeControl.isExpanded(node)) {
                  this.treeControl.expand(node);
                  restored = true;
                }
              }
            });

            if (restored) {
              this.cd.detectChanges();
            }
          } else if (attempts < maxAttempts) {
            requestAnimationFrame(tryRestoreExpansion);
          }
        };

        tryRestoreExpansion();
      });
    }
  }

  private updateParentNodeChildrenInPlace(
    parentNodeId: string,
    loadedChildren: FlatProject[]
  ): void {
    const dataNodes = this.treeControl.dataNodes;

    const expandedNodeIds = new Set<string>();
    this.treeControl.expansionModel.selected.forEach((node) => {
      expandedNodeIds.add(node.id);
    });

    let parentNode: FlatProject | null = null;
    for (const node of dataNodes) {
      if (node.id === parentNodeId) {
        parentNode = node;
        break;
      }
    }

    if (parentNode && parentNode.children) {
      loadedChildren.forEach((loadedChild) => {
        const existingChild = parentNode!.children!.find(
          (c) => c.id === loadedChild.id
        );
        if (existingChild) {
          Object.assign(existingChild, loadedChild);
        }
      });

      this.cd.detectChanges();

      requestAnimationFrame(() => {
        this.treeControl.dataNodes.forEach((node) => {
          if (expandedNodeIds.has(node.id) && node.expandable) {
            if (!this.treeControl.isExpanded(node)) {
              this.treeControl.expand(node);
            }
          }
        });
        this.cd.detectChanges();
      });
    }
  }

  private updateNodeChildrenInTree(
    tree: FlatProject[],
    nodeId: string,
    loadedChildren: FlatProject[]
  ): boolean {
    for (let i = 0; i < tree.length; i++) {
      if (tree[i].id === nodeId) {
        const existingChildrenIds = new Set(
          (tree[i].children || []).map((c) => c.id)
        );
        const newChildren = loadedChildren.filter(
          (c) => !existingChildrenIds.has(c.id)
        );
        const allChildren = [...(tree[i].children || []), ...newChildren];

        const loadedChildrenIds = new Set(loadedChildren.map((c) => c.id));
        const remainingUnloadedChildren = (
          tree[i].unloadedChildren || []
        ).filter((child: ChildProject) => !loadedChildrenIds.has(child.id));

        const allChildrenLoaded = remainingUnloadedChildren.length === 0;

        tree[i] = {
          ...tree[i],
          children: allChildren,
          unloadedChildren:
            remainingUnloadedChildren.length > 0
              ? remainingUnloadedChildren
              : undefined,
          childrenLoaded: allChildrenLoaded,
          expandable:
            allChildren.length > 0 || remainingUnloadedChildren.length > 0
        };
        return true;
      }
      if (tree[i].children && tree[i].children.length > 0) {
        if (
          this.updateNodeChildrenInTree(
            tree[i].children!,
            nodeId,
            loadedChildren
          )
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private convertProjectsToFlatProjects(
    projects: Project[]
  ): FlatProject[] {
    if (!projects || projects.length === 0) {
      return [];
    }

    return projects.map((project) => {
      const projectId = project.project_id || project.id;
      const children = (project.children || []) as ChildProject[];
      const activeChildren = children.filter(
        (child: ChildProject) => !child.is_archived
      );

      return {
        id: project.id,
        name: project.name,
        path: project.path || '',
        project_id: projectId,
        is_project: project.is_project || false,
        children: [],
        unloadedChildren: activeChildren,
        childrenLoaded: false,
        level: 0,
        expandable: activeChildren.length > 0
      };
    });
  }

  hasChild = (_: number, node: FlatProject) => {
    return node.expandable || false;
  };

  public onTreeNodeSelect(node: FlatProject): void {
    if (this.isDisabled(node)) {
      return;
    }

    if (this.data.itemType !== 'folder' && node.id === this.currentPath) {
      return;
    }

    this.selectedFolder = {
      id: node.id,
      name: node.name,
      path: node.path,
      project_id: node.project_id,
      is_project: node.is_project
    } as Project;
    this.cd.detectChanges();
  }

  public onNodeToggle(node: FlatProject, event: Event): void {
    event.stopPropagation();
  }

  private isDescendant(node: FlatProject, ancestorId: string): boolean {
    if (!ancestorId) {
      return false;
    }

    if (node.id === ancestorId) {
      return false;
    }

    const allNodes = this.treeControl.dataNodes;
    const ancestorNode = allNodes.find((n) => n.id === ancestorId);
    if (!ancestorNode) {
      return false;
    }

    let currentNode: FlatProject | null = node;
    while (currentNode && currentNode.level > ancestorNode.level) {
      const currentIndex = allNodes.indexOf(currentNode);
      const parentLevel = currentNode.level - 1;

      for (let i = currentIndex - 1; i >= 0; i--) {
        if (allNodes[i].level === parentLevel) {
          currentNode = allNodes[i];
          if (currentNode.id === ancestorId) {
            return true;
          }
          break;
        }
        if (allNodes[i].level < parentLevel) {
          return false;
        }
      }

      if (currentIndex === allNodes.indexOf(currentNode)) {
        break;
      }
    }

    return false;
  }

  private findNodeById(id: string): FlatProject | null {
    const allNodes = this.treeControl.dataNodes;
    return allNodes.find((node) => node.id === id) || null;
  }

  public isSelected(node: FlatProject): boolean {
    return this.selectedFolder?.id === node.id;
  }

  public isDisabled(node: FlatProject): boolean {
    if (this.data.itemType === 'folder' && this.data.itemId) {
      if (node.id === this.data.itemId) {
        return true;
      }
      if (this.isDescendant(node, this.data.itemId)) {
        return true;
      }
      return false;
    }

    if (node.id === this.currentPath && this.data.itemType !== 'folder') {
      return true;
    }

    return false;
  }

  public canMove(): boolean {
    if (!this.selectedFolder) {
      return false;
    }
    const selectedNode = this.findNodeById(this.selectedFolder.id);
    return selectedNode !== null && !this.isDisabled(selectedNode);
  }

  public getDialogTitle(): string {
    if (this.data.itemType === 'areas') {
      const count = this.data.itemsCount || this.data.itemIds?.length || 0;
      return `Select where you want to move ${count} area${
        count > 1 ? 's' : ''
      }`;
    } else if (this.data.itemType === 'area') {
      return 'Select where you want to move this area';
    } else {
      return 'Select where you want to move this folder';
    }
  }

  public getMoveButtonText(): string {
    if (this.data.itemType === 'areas') {
      return 'Move Areas';
    } else if (this.data.itemType === 'area') {
      return 'Move Area';
    } else {
      return 'Move Folder';
    }
  }

  public move(): void {
    if (!this.selectedFolder) {
      return;
    }

    const selectedNode = this.findNodeById(this.selectedFolder.id);
    if (!selectedNode) {
      return;
    }

    const destinationFolder: Project = {
      ...this.selectedFolder,
      project_id: selectedNode.project_id,
      id: this.selectedFolder.id,
      name: this.selectedFolder.name,
      path: this.selectedFolder.path || '',
      is_project: selectedNode.is_project
    } as Project;

    this.dialogRef.close({
      destinationFolderId: this.selectedFolder.id,
      destinationFolder
    });
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public isCrossProjectMove(): boolean {
    if (!this.selectedFolder || !this.currentPath) {
      return false;
    }

    const selectedNode = this.findNodeById(this.selectedFolder.id);
    const currentNode = this.findNodeById(this.currentPath);

    if (!selectedNode || !currentNode) {
      return false;
    }

    return currentNode.project_id !== selectedNode.project_id;
  }
}
